import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Map from "devextreme-react/map";
import type { ClickEvent } from "devextreme/ui/map";

import { fetchWithToken } from "src/hooks/useFetch";
import { useAuth } from "src/contexts/auth";

// Same browser key the fleet map already uses (customer-map.js).
const GOOGLE_MAPS_API_KEY = "AIzaSyBsbjyww1pLMBNpntQJal1Grq7Tp2Z05pE";

export type GeocodeResult = {
  displayName: string;
  lat: number;
  lon: number;
  country: string;
  city: string;
  region: string;
};

export type LocationInstrument = {
  instrumentID: string;
  instrumentName?: string;
  serialNumber?: string;
  lat?: number;
  lon?: number;
  city?: string;
  country?: string;
  region?: string;
  locationSource?: string;
};

type LocationPickerProps = {
  instrument: LocationInstrument;
  onClose: () => void;
  onSaved: () => void;
};

export function hasCoordinates(lat?: number, lon?: number): boolean {
  if (lat == null || lon == null || Number.isNaN(lat) || Number.isNaN(lon)) {
    return false;
  }
  if (lat <= -90 || lat > 90 || lon < -180 || lon > 180) {
    return false;
  }
  if (lat === 0 && lon === 0) {
    return false;
  }
  return true;
}

export function formatInstrumentLocation(row: {
  city?: string;
  region?: string;
  country?: string;
  locationSource?: string;
}): string {
  const city = (row.city || "").trim();
  const region = (row.region || "").trim();
  const country = (row.country || "").trim();
  let label = "";
  if (country === "United States" && city && region) {
    label = `${city}, ${region}`;
  } else if (city && country) {
    label = `${city}, ${country}`;
  } else {
    label = city || country;
  }
  if (label && row.locationSource === "user") {
    return `${label} (manual)`;
  }
  return label;
}

function parseMapLocation(location: unknown): { lat: number; lon: number } | null {
  if (location == null) {
    return null;
  }
  if (Array.isArray(location) && location.length >= 2) {
    const lat = Number(location[0]);
    const lon = Number(location[1]);
    return hasCoordinates(lat, lon) ? { lat, lon } : null;
  }
  if (typeof location === "string") {
    const [latStr, lonStr] = location.split(",");
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    return hasCoordinates(lat, lon) ? { lat, lon } : null;
  }
  if (typeof location === "object") {
    const loc = location as { lat?: number; lng?: number; lon?: number };
    const lat = loc.lat;
    const lon = loc.lng ?? loc.lon;
    return hasCoordinates(lat, lon) ? { lat: lat as number, lon: lon as number } : null;
  }
  return null;
}

export default function LocationPicker({
  instrument,
  onClose,
  onSaved,
}: LocationPickerProps) {
  const { getAccessTokenSilently } = useAuth();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [lat, setLat] = useState(instrument.lat);
  const [lon, setLon] = useState(instrument.lon);
  const [city, setCity] = useState(instrument.city || "");
  const [region, setRegion] = useState(instrument.region || "");
  const [country, setCountry] = useState(instrument.country || "");

  const pinned = hasCoordinates(lat, lon);
  const mapRef = useRef<Map>(null);

  // Controlled center/zoom fight Google Maps: bounds_changed updates the widget,
  // then React pushes the old pin back and DevExtreme serializes those setCenter
  // calls. Same pattern as customer-map.js — defaults only, then pan via instance.
  const [{ defaultCenter, defaultZoom }] = useState(() => {
    if (hasCoordinates(instrument.lat, instrument.lon)) {
      return {
        defaultCenter: { lat: instrument.lat as number, lng: instrument.lon as number },
        defaultZoom: 12,
      };
    }
    return {
      defaultCenter: { lat: 40.7061, lng: -73.9969 },
      defaultZoom: 3,
    };
  });

  const panTo = useCallback((nextLat: number, nextLon: number, nextZoom = 12) => {
    const map = mapRef.current?.instance;
    if (!map) {
      return;
    }
    map.option("center", { lat: nextLat, lng: nextLon });
    map.option("zoom", nextZoom);
  }, []);

  const applyResult = useCallback((result: GeocodeResult) => {
    setLat(result.lat);
    setLon(result.lon);
    setCity(result.city || "");
    setRegion(result.region || "");
    setCountry(result.country || "");
    setQuery(result.displayName || "");
    setError("");
    panTo(result.lat, result.lon);
  }, [panTo]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = (await fetchWithToken(
          getAccessTokenSilently,
          `/geocode?q=${encodeURIComponent(q)}`
        )) as { results?: GeocodeResult[] };
        if (!cancelled) {
          setSearchResults(data.results ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setSearchResults([]);
          setError(err instanceof Error ? err.message : "Search failed");
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, getAccessTokenSilently]);

  const reverseLookup = useCallback(
    async (nextLat: number, nextLon: number) => {
      setLat(nextLat);
      setLon(nextLon);
      setError("");
      try {
        const result = (await fetchWithToken(
          getAccessTokenSilently,
          `/reverseGeocode?lat=${encodeURIComponent(String(nextLat))}&lon=${encodeURIComponent(
            String(nextLon)
          )}`
        )) as GeocodeResult;
        setCity(result.city || "");
        setRegion(result.region || "");
        setCountry(result.country || "");
        if (result.displayName) {
          setQuery(result.displayName);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Reverse geocode failed");
      }
    },
    [getAccessTokenSilently]
  );

  const onMapClick = useCallback(
    (e: ClickEvent) => {
      const parsed = parseMapLocation((e as ClickEvent & { location?: unknown }).location);
      if (!parsed) {
        return;
      }
      void reverseLookup(parsed.lat, parsed.lon);
    },
    [reverseLookup]
  );

  const save = useCallback(async () => {
    if (!hasCoordinates(lat, lon)) {
      setError("Pick a place on the map or from search before saving");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await fetchWithToken(
        getAccessTokenSilently,
        `/instrument/${instrument.serialNumber}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            lat,
            lon,
            city,
            region,
            country,
          }),
        }
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    lat,
    lon,
    city,
    region,
    country,
    getAccessTokenSilently,
    instrument.serialNumber,
    onSaved,
    onClose,
  ]);

  const resetToIp = useCallback(async () => {
    if (
      !window.confirm(
        "Clear the manual pin and go back to the IP-based location?"
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await fetchWithToken(
        getAccessTokenSilently,
        `/instrument/${instrument.serialNumber}`,
        {
          method: "PATCH",
          body: JSON.stringify({ resetLocation: true }),
        }
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setSaving(false);
    }
  }, [getAccessTokenSilently, instrument.serialNumber, onSaved, onClose]);

  const markers = useMemo(
    () => (pinned ? [{ location: { lat: lat as number, lng: lon as number } }] : []),
    [pinned, lat, lon]
  );

  const titleName = instrument.instrumentName || instrument.serialNumber || instrument.instrumentID;
  const summary = formatInstrumentLocation({ city, region, country });

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Location — {titleName}</DialogTitle>
      <DialogContent sx={{ overflow: "hidden" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Search for a place or click the map. The pin is saved as a manual
          location and the nightly IP lookup will not overwrite it.
        </Typography>
        <Autocomplete
          freeSolo
          filterOptions={(options) => options}
          options={searchResults}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.displayName
          }
          inputValue={query}
          loading={searching}
          noOptionsText={query.trim().length < 2 ? "Type to search" : "No places found"}
          onInputChange={(_event, value, reason) => {
            if (reason === "reset") {
              return;
            }
            setQuery(value);
          }}
          onChange={(_event, value) => {
            if (value && typeof value !== "string") {
              applyResult(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search address or place"
              size="small"
            />
          )}
        />
        <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }} data-testid="location-summary">
          {pinned ? summary || `${Number(lat).toFixed(4)}, ${Number(lon).toFixed(4)}` : "No pin yet"}
        </Typography>
        <Box sx={{ height: 400, width: "100%" }} data-testid="location-map">
          <Map
            ref={mapRef}
            provider="google"
            apiKey={GOOGLE_MAPS_API_KEY}
            type="roadmap"
            height="100%"
            width="100%"
            controls
            autoAdjust={false}
            defaultZoom={defaultZoom}
            defaultCenter={defaultCenter}
            markers={markers}
            onClick={onMapClick}
          />
        </Box>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
        <Button onClick={resetToIp} disabled={saving} color="inherit">
          Reset to IP location
        </Button>
        <Box>
          <Button onClick={onClose} disabled={saving} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !pinned} variant="contained">
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
